import type { ListResult, PaginationMeta } from "../api";

export type { ListResult, PaginationMeta };

export type RoleName =
  | "landlord"
  | "buyer"
  | "tenant"
  | "agent"
  | "lawyer"
  | "valuer";

export interface RoleResource {
  id: string | number;
  status?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
  [key: string]: unknown;
}

export interface ListQuery {
  page?: number;
  per_page?: number;
  search?: string;
  status?: string;
  [key: string]: unknown;
}

export const LANDLORD_ENDPOINTS = {
  properties: "/user/property",
  propertyBookings: "/user/property/my-bookings",
  agencyAgreements: "/user/property/agency-agreements",
  enquiries: "/user/landlord/enquiries",
  tenancies: "/user/landlord/tenancies",
  rentReceipts: "/user/landlord/rent-receipts",
  rentReminders: "/user/landlord/rent-reminders",
  rentWallets: "/user/landlord/rent-wallets",
  notices: "/user/landlord/notices",
  offers: "/user/landlord/transaction/offers",
  lois: "/user/landlord/transaction/lois",
  dealRooms: "/user/landlord/transaction/deal-room",
  contracts: "/user/landlord/transaction/contracts",
} as const;

export const BUYER_ENDPOINTS = {
  discoverProperties: "/user/buyer/discover-properties",
  enquiries: "/user/buyer/my-enquiries",
  bookingsViewings: "/user/buyer/bookings-viewings",
  inspections: "/user/buyer/inspections",
  offers: "/user/buyer/my-offers",
  lois: "/user/buyer/lois",
  dealRooms: "/user/buyer/deal-rooms",
  dueDiligence: "/user/buyer/due-diligence",
  contracts: "/user/buyer/contracts",
} as const;

export const TENANT_ENDPOINTS = {
  discoverProperties: "/user/tenant/discover-properties",
  enquiries: "/user/tenant/my-enquiries",
  bookingsViewings: "/user/tenant/bookings-viewings",
  rentWallets: "/user/tenant/rent-wallets",
  rentReceipts: "/user/tenant/rent-receipts",
  noticesRenewals: "/user/tenant/notices-renewals",
  agreements: "/user/tenant/agreements",
} as const;

export const AGENT_ENDPOINTS = {
  availableListings: "/user/agent/available-listings",
  assignments: "/user/agent/assignments",
  agencyAgreements: "/user/agent/agency-agreements",
  enquiries: "/user/agent/enquiries",
  bookings: "/user/agent/bookings",
  inspections: "/user/agent/inspections",
  dealRooms: "/user/agent/deal-rooms",
  offers: "/user/agent/offers",
  commissions: "/user/agent/commissions",
  payouts: "/user/agent/payouts",
} as const;

export const LAWYER_ENDPOINTS = {
  engagements: "/user/lawyer/engagements",
  dealRooms: "/user/lawyer/deal-room",
  dueDiligence: "/user/lawyer/due-diligence",
  propertySearches: "/user/lawyer/property-searches",
  negotiations: "/user/lawyer/negotiations",
  contracts: "/user/lawyer/contracts",
  tenancyAgreements: "/user/lawyer/tenancy-agreements",
  deeds: "/user/lawyer/deeds",
  documents: "/user/lawyer/documents",
} as const;

export const VALUER_ENDPOINTS = {
  assignedValuations: "/user/valuer/assigned-valuations",
  valuationReports: "/user/valuer/valuation-reports",
  dealRoomDocuments: "/user/valuer/deal-room-documents",
} as const;

export type LandlordResourceKey = keyof typeof LANDLORD_ENDPOINTS;
export type BuyerResourceKey = keyof typeof BUYER_ENDPOINTS;
export type TenantResourceKey = keyof typeof TENANT_ENDPOINTS;
export type AgentResourceKey = keyof typeof AGENT_ENDPOINTS;
export type LawyerResourceKey = keyof typeof LAWYER_ENDPOINTS;
export type ValuerResourceKey = keyof typeof VALUER_ENDPOINTS;

export const ROLE_ENDPOINTS: Record<RoleName, Record<string, string>> = {
  landlord: LANDLORD_ENDPOINTS,
  buyer: BUYER_ENDPOINTS,
  tenant: TENANT_ENDPOINTS,
  agent: AGENT_ENDPOINTS,
  lawyer: LAWYER_ENDPOINTS,
  valuer: VALUER_ENDPOINTS,
};

// Collection keys the API may wrap a list in when it is not a plain `data` array.
export const ROLE_COLLECTION_KEYS: Record<RoleName, Record<string, string[]>> = {
  landlord: {
    properties: ["properties", "listings"],
    propertyBookings: ["bookings", "property_bookings"],
    agencyAgreements: ["agreements", "agency_agreements"],
    enquiries: ["enquiries"],
    tenancies: ["tenancies"],
    rentReceipts: ["receipts", "rent_receipts"],
    rentReminders: ["reminders", "rent_reminders"],
    rentWallets: ["wallets", "rent_wallets"],
    notices: ["notices"],
    offers: ["offers"],
    lois: ["lois"],
    dealRooms: ["deal_rooms", "dealRooms", "rooms"],
    contracts: ["contracts"],
  },
  buyer: {
    discoverProperties: ["properties", "listings"],
    enquiries: ["enquiries"],
    bookingsViewings: ["bookings", "viewings", "property_bookings"],
    inspections: ["inspections"],
    offers: ["offers"],
    lois: ["lois"],
    dealRooms: ["deal_rooms", "dealRooms", "rooms"],
    dueDiligence: ["due_diligence", "reports"],
    contracts: ["contracts"],
  },
  tenant: {
    discoverProperties: ["properties", "listings"],
    enquiries: ["enquiries"],
    bookingsViewings: ["bookings", "viewings", "property_bookings"],
    rentWallets: ["wallets", "rent_wallets"],
    rentReceipts: ["receipts", "rent_receipts"],
    noticesRenewals: ["notices", "renewals", "notices_renewals"],
    agreements: ["agreements"],
  },
  agent: {
    availableListings: ["listings", "properties"],
    assignments: ["assignments"],
    agencyAgreements: ["agreements", "agency_agreements"],
    enquiries: ["enquiries"],
    bookings: ["bookings"],
    inspections: ["inspections"],
    dealRooms: ["deal_rooms", "dealRooms", "rooms"],
    offers: ["offers"],
    commissions: ["commissions"],
    payouts: ["payouts"],
  },
  lawyer: {
    engagements: ["engagements"],
    dealRooms: ["deal_rooms", "dealRooms", "rooms"],
    dueDiligence: ["due_diligence", "reports"],
    propertySearches: ["searches", "property_searches"],
    negotiations: ["negotiations"],
    contracts: ["contracts"],
    tenancyAgreements: ["agreements", "tenancy_agreements"],
    deeds: ["deeds"],
    documents: ["documents"],
  },
  valuer: {
    assignedValuations: ["valuations", "assignments"],
    valuationReports: ["reports", "valuation_reports"],
    dealRoomDocuments: ["documents", "deal_room_documents"],
  },
};
